import { actx, drum, pluck, midiToHz } from "./musicAudio";

const _ = null;
export const DRUM_LOOPS = [
  { id: 0, name: "Swing City", desc: "The New Jack pocket — swung hats, clap on 2 & 4.", pat: { kick: [0, 7, 10], snare: [4, 12], clap: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14], ohat: [7, 15] } },
  { id: 1, name: "Boom Bap", desc: "Dusty, heavy, head-nod tempo.", pat: { kick: [0, 7, 10], snare: [4, 12], hat: [0, 2, 4, 6, 8, 10, 12, 14] } },
  { id: 2, name: "Trap Lite", desc: "Fast hats, deep 808.", pat: { kick: [0, 3, 8, 11], snare: [4, 12], hat: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15], 808: [0, 8] } },
  { id: 3, name: "Disco Bounce", desc: "Four on the floor, open hats between.", pat: { kick: [0, 4, 8, 12], ohat: [2, 6, 10, 14], clap: [4, 12], perc: [3, 11] } },
];
export const BASS_LOOPS = [
  { id: 0, name: "Round Bass", desc: "Simple root-fifth groove.", notes: [48, _, _, 48, _, _, 51, _, 48, _, _, _, 55, _, 53, _] },
  { id: 1, name: "Walking", desc: "Steps up and back down the scale.", notes: [48, _, 50, _, 51, _, 53, _, 55, _, 53, _, 51, _, 50, _] },
  { id: 2, name: "Sub Pulse", desc: "Sparse and deep — leaves room for the drums.", notes: [48, _, _, _, _, _, 48, _, _, _, 48, _, _, _, 46, _] },
  { id: 3, name: "Funk Pop", desc: "Busy, syncopated, octave jumps.", notes: [48, 48, _, 60, _, 48, _, 55, _, _, 48, _, 53, _, 55, _] },
];
export const MELODY_LOOPS = [
  { id: 0, name: "The Hook", desc: "A singable pentatonic line.", notes: [72, _, 75, _, 77, _, _, 79, _, 77, _, 75, _, 72, _, _] },
  { id: 1, name: "Keyboard Stabs", desc: "Chord hits on the off-beats.", notes: [_, _, [72, 75, 79], _, _, _, [70, 74, 77], _, _, _, [72, 75, 79], _, [75, 79, 82], _, _, _] },
  { id: 2, name: "Arpeggio", desc: "Rolling up and down the chord.", notes: [72, 75, 79, 82, 79, 75, 72, 75, 79, 82, 84, 82, 79, 75, 72, 70] },
  { id: 3, name: "Bell Tone", desc: "High, slow, spacious.", notes: [84, _, _, 82, _, _, 79, _, _, _, 77, _, 79, _, _, _] },
];
export const CLUB_TRACKS = [
  { id: "club1", title: "Swing City Nights", artist: "DJ Quackstar", bpm: 108, loops: { drums: 0, bass: 0, melody: 0 } },
  { id: "club2", title: "Boulevard Bounce", artist: "DJ Quackstar", bpm: 118, loops: { drums: 3, bass: 3, melody: 2 } },
];

export function bass(midi, when, dur = 0.22, vol = 1) {
  const c = actx(), t = when ?? c.currentTime, f = midiToHz(midi);
  const o = c.createOscillator(), o2 = c.createOscillator(), lp = c.createBiquadFilter(), g = c.createGain();
  o.type = "sawtooth"; o.frequency.value = f; o2.type = "sine"; o2.frequency.value = f / 2;
  lp.type = "lowpass"; lp.frequency.setValueAtTime(900, t); lp.frequency.exponentialRampToValueAtTime(180, t + dur); lp.Q.value = 2;
  g.gain.setValueAtTime(0, t); g.gain.linearRampToValueAtTime(0.5 * vol, t + 0.006); g.gain.setValueAtTime(0.5 * vol, t + dur * 0.6); g.gain.exponentialRampToValueAtTime(0.001, t + dur + 0.08);
  o.connect(lp); o2.connect(lp); lp.connect(g).connect(c.destination);
  o.start(t); o2.start(t); o.stop(t + dur + 0.1); o2.stop(t + dur + 0.1);
}

export function playStep(song, step, when, mix) {
  const d = DRUM_LOOPS[song.loops.drums]?.pat || {}, b = BASS_LOOPS[song.loops.bass]?.notes || [], m = MELODY_LOOPS[song.loops.melody]?.notes || [];
  const i = step % 16; const lv = (k) => { const t = mix?.[k]; if (!t) return 1; if (t.mute) return 0; if (mix.solo && mix.solo !== k) return 0; return t.vol ?? 1; };
  const dv = lv("drums"), bv = lv("bass"), mv = lv("melody");
  if (dv > 0) for (const k of Object.keys(d)) if (d[k].includes(i)) drum(k, when, (k === "hat" ? 0.6 : 1) * dv);
  if (bv > 0 && b[i] != null) bass(b[i], when, 0.22, bv);
  const mel = m[i];
  if (mv > 0 && mel != null) (Array.isArray(mel) ? mel : [mel]).forEach((n) => pluck(n, 0.55 * mv, when));
}

export function createSequencer(song, { onStep, bars = Infinity, swing = 0.12, mix = null } = {}) {
  const c = actx(); const stepDur = 60 / song.bpm / 4; let step = 0, start = 0, timer = null, done = false;
  const timeOf = (s) => start + s * stepDur + (s % 2 ? stepDur * swing : 0);
  const tick = () => {
    while (!done && timeOf(step) < c.currentTime + 0.12) {
      if (step >= bars * 16) { done = true; break; }
      const when = timeOf(step); playStep(song, step, when, mix?.current || mix); onStep?.(step, when); step++;
    }
  };
  return {
    start() { start = c.currentTime + 0.08; step = 0; done = false; timer = setInterval(tick, 25); tick(); return start; },
    stepNow: () => Math.max(0, Math.floor((c.currentTime - start) / stepDur)),
    stop() { clearInterval(timer); timer = null; done = true; },
    startTime: () => start, stepDur, timeOf, isDone: () => done && timeOf(step) < c.currentTime,
  };
}

const seeded = (n) => { let s = n * 9301 + 49297; return () => { s = (s * 9301 + 49297) % 233280; return s / 233280; }; };
export function chartFor(song, bars) {
  const d = DRUM_LOOPS[song.loops.drums]?.pat || {}, b = BASS_LOOPS[song.loops.bass]?.notes || [], m = MELODY_LOOPS[song.loops.melody]?.notes || [];
  const rnd = seeded(song.bpm + song.loops.drums * 7 + song.loops.bass * 13 + song.loops.melody * 17); const notes = [];
  for (let bar = 1; bar < bars; bar++) {
    const core = [], extra = [];
    for (let i = 0; i < 16; i++) {
      const s = bar * 16 + i;
      if ((d.kick || []).includes(i)) core.push({ s, lane: 0 });
      else if ((d.snare || d.clap || []).includes(i)) core.push({ s, lane: 1 });
      if (i % 2 === 0 && b[i] != null && rnd() < 0.5) extra.push({ s, lane: 2 });
      if (i % 2 === 0 && m[i] != null && rnd() < 0.45) extra.push({ s, lane: 3 });
    }
    while (core.length + extra.length > 9) extra.splice(Math.floor(rnd() * extra.length), 1);
    notes.push(...core, ...extra);
  }
  return notes.sort((a, b2) => a.s - b2.s);
}
